import { useEffect, useLayoutEffect, useState } from "react";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { useCompleteTutorial } from "../api/quests";

// One step of the post-registration onboarding tour. If `targetSelector` is
// set, the step dims everything except that DOM element and pops a tooltip
// next to it. If null, renders a centered modal (used for welcome / final).
export interface TutorialStep {
  id: string;
  title: string;
  body: string;
  // CSS selector resolved at runtime via document.querySelector. The element
  // must be mounted before the step renders — anchor screens (Castle, etc.)
  // should mount before the tutorial fires.
  targetSelector?: string;
  // Whether to set an active tab before showing this step (so the target is
  // actually on screen). Matched by App.tsx onSetActiveTab callback.
  switchToTab?: "castle" | "army" | "me" | "marches" | "quests";
}

interface Props {
  steps: TutorialStep[];
  onSwitchTab: (tab: NonNullable<TutorialStep["switchToTab"]>) => void;
  onComplete: () => void;
}

export function TutorialOverlay({ steps, onSwitchTab, onComplete }: Props) {
  const [idx, setIdx] = useState(0);
  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const completeTutorial = useCompleteTutorial();

  // Switch to the screen that hosts this step's target BEFORE we try to find
  // the DOM node. Effect runs whenever the step changes.
  useEffect(() => {
    if (step.switchToTab) onSwitchTab(step.switchToTab);
  }, [step, onSwitchTab]);

  const advance = () => {
    hapticImpact("light");
    if (isLast) {
      completeTutorial.mutate(undefined, {
        onSuccess: () => {
          hapticNotification("success");
          onComplete();
        },
      });
    } else {
      setIdx(idx + 1);
    }
  };

  const skip = () => {
    hapticImpact("light");
    completeTutorial.mutate(undefined, {
      onSuccess: onComplete,
    });
  };

  return (
    <div className="fixed inset-0 z-50">
      {step.targetSelector ? (
        <SpotlightStep
          selector={step.targetSelector}
          step={step}
          isLast={isLast}
          onNext={advance}
          onSkip={skip}
          stepNumber={idx + 1}
          totalSteps={steps.length}
        />
      ) : (
        <CenterModal
          step={step}
          isLast={isLast}
          onNext={advance}
          onSkip={skip}
          stepNumber={idx + 1}
          totalSteps={steps.length}
        />
      )}
    </div>
  );
}

// Polls querySelector until the element is found (or gives up after ~2s).
// The Castle / Army / etc. screens may mount asynchronously after the tab
// switch, so we can't grab the rect on first paint.
function useTargetRect(selector: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (el) {
        setRect(el.getBoundingClientRect());
        return;
      }
      attempts++;
      if (attempts < 30) {
        setTimeout(tick, 60);
      }
    };
    tick();

    const update = () => {
      const el = document.querySelector(selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [selector]);

  return rect;
}

function SpotlightStep({
  selector,
  step,
  isLast,
  onNext,
  onSkip,
  stepNumber,
  totalSteps,
}: {
  selector: string;
  step: TutorialStep;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
  stepNumber: number;
  totalSteps: number;
}) {
  const rect = useTargetRect(selector);

  // If the target isn't visible yet, fall back to a centered modal so the
  // user isn't staring at a frozen dim layer.
  if (!rect) {
    return (
      <CenterModal
        step={step}
        isLast={isLast}
        onNext={onNext}
        onSkip={onSkip}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
      />
    );
  }

  const pad = 8;
  const sl = Math.max(0, rect.left - pad);
  const st = Math.max(0, rect.top - pad);
  const sr = sl + rect.width + pad * 2;
  const sb = st + rect.height + pad * 2;

  // Tooltip placement: prefer below if the spotlit element has room beneath
  // it, else above if there's room there. For tall elements that fill the
  // viewport (e.g. the buildings list) neither has room, so we anchor the
  // tooltip near the bottom of the screen as a fallback — it overlays the
  // bottom of the spotlit area, which is fine since the area stays visible.
  const TOOLTIP_HEIGHT_ESTIMATE = 220;
  const spaceBelow = window.innerHeight - sb;
  const spaceAbove = st;
  let placement: "below" | "above" | "fallback-bottom";
  if (spaceBelow >= TOOLTIP_HEIGHT_ESTIMATE) {
    placement = "below";
  } else if (spaceAbove >= TOOLTIP_HEIGHT_ESTIMATE) {
    placement = "above";
  } else {
    placement = "fallback-bottom";
  }

  return (
    <>
      {/* Four dim rectangles around the spotlit area — leaves a clean cut-out
          around the targeted element without needing SVG masks. */}
      <div
        className="absolute bg-black/75"
        style={{ left: 0, top: 0, right: 0, height: st }}
      />
      <div
        className="absolute bg-black/75"
        style={{ left: 0, top: sb, right: 0, bottom: 0 }}
      />
      <div
        className="absolute bg-black/75"
        style={{ left: 0, top: st, width: sl, height: sb - st }}
      />
      <div
        className="absolute bg-black/75"
        style={{ left: sr, top: st, right: 0, height: sb - st }}
      />
      {/* Glowing outline around the target so the eye snaps to it. */}
      <div
        className="pointer-events-none absolute rounded-lg ring-2 ring-white/80"
        style={{
          left: sl,
          top: st,
          width: sr - sl,
          height: sb - st,
          boxShadow: "0 0 0 4px rgba(255,255,255,0.15), 0 0 24px rgba(255,255,255,0.4)",
        }}
      />
      {/* Skip in the corner. */}
      <button
        type="button"
        onClick={onSkip}
        className="absolute right-3 top-3 rounded-md bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur"
      >
        Пропустить
      </button>
      {/* Tooltip — positioned above / below / floating at bottom depending on
          available space (see placement logic above). */}
      <div
        className="absolute mx-3 max-w-md rounded-xl bg-tg-secondaryBg p-4 shadow-2xl"
        style={
          placement === "below"
            ? { left: 0, right: 0, top: sb + 12 }
            : placement === "above"
              ? { left: 0, right: 0, bottom: window.innerHeight - st + 12 }
              : { left: 0, right: 0, bottom: 16 }
        }
      >
        <TutorialContent
          step={step}
          isLast={isLast}
          onNext={onNext}
          stepNumber={stepNumber}
          totalSteps={totalSteps}
        />
      </div>
    </>
  );
}

function CenterModal({
  step,
  isLast,
  onNext,
  onSkip,
  stepNumber,
  totalSteps,
}: {
  step: TutorialStep;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
  stepNumber: number;
  totalSteps: number;
}) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/75">
      <button
        type="button"
        onClick={onSkip}
        className="absolute right-3 top-3 rounded-md bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur"
      >
        Пропустить
      </button>
      <div className="mx-4 max-w-md rounded-2xl bg-tg-secondaryBg p-5 shadow-2xl">
        <TutorialContent
          step={step}
          isLast={isLast}
          onNext={onNext}
          stepNumber={stepNumber}
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
}

function TutorialContent({
  step,
  isLast,
  onNext,
  stepNumber,
  totalSteps,
}: {
  step: TutorialStep;
  isLast: boolean;
  onNext: () => void;
  stepNumber: number;
  totalSteps: number;
}) {
  return (
    <>
      <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
        Шаг {stepNumber} из {totalSteps}
      </div>
      <div className="mb-2 text-lg font-semibold leading-tight">
        {step.title}
      </div>
      <div className="mb-4 text-sm leading-relaxed text-tg-text/90">
        {step.body}
      </div>
      <button
        type="button"
        onClick={onNext}
        className="w-full rounded-lg bg-tg-button px-3 py-2.5 text-sm font-medium text-tg-buttonText"
      >
        {isLast ? "Начать игру" : "Дальше"}
      </button>
    </>
  );
}
